import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useAuth } from '../context/EnterpriseAuthContext';
import { RouteLoading } from './ProtectedRoute';

/**
 * Requires a signed-in user whose membership in the ACTIVE account has a role:
 * 'template_creator' / 'local_admin' (both mean "can manage templates") or 'account_admin'.
 * Only the active account's membership counts (never the global profiles.role), and an
 * unknown role denies access. This shapes the UI only: RLS and the edge functions enforce.
 * @param {{children: React.ReactNode, requiredRole: string}} props - Props.
 * @returns {JSX.Element} Children, a redirect, a loading state or a no-access message.
 */
const RoleBasedProtectedRoute = ({ children, requiredRole }) => {
  const { state, actions } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  if (state.loading) return <RouteLoading />;

  if (state.requiresPasswordReset && location.pathname !== '/reset-password') {
    return <Navigate to="/reset-password" replace />;
  }

  if (!state.isAuthenticated) {
    return <Navigate to="/login" state={{ from: `${location.pathname}${location.search}${location.hash}` }} replace />;
  }

  const hasPermission = () => {
    if (!state.activeAccount || !state.currentMembership) return false;
    if (state.currentMembership.account_id && state.currentMembership.account_id !== state.activeAccount.id) return false;
    if (requiredRole === 'template_creator' || requiredRole === 'local_admin') {
      return actions.canManageTemplates();
    }
    if (requiredRole === 'account_admin') {
      return actions.isAccountAdmin();
    }
    return false;
  };

  if (!hasPermission()) {
    return (
      <Box sx={{ minHeight: 'var(--q-main-height, 60vh)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Box
          role="alert"
          sx={{
            maxWidth: 440,
            textAlign: 'center',
            p: 4,
            bgcolor: 'var(--q-surface, #FFFFFF)',
            border: '1px solid var(--q-border, #E3E0DA)',
            borderRadius: 'var(--q-radius-card, 16px)',
          }}
        >
          <Box sx={{ width: 44, height: 44, mx: 'auto', mb: 2, borderRadius: '12px', bgcolor: 'var(--q-subtle, #F1EFEB)', color: 'var(--q-muted, #5E5A54)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LockOutlined />
          </Box>
          <Typography component="h1" sx={{ fontSize: 18, fontWeight: 600, mb: 1 }}>
            You don&apos;t have access to this page
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--q-muted, #5E5A54)', mb: 3 }}>
            {requiredRole === 'account_admin'
              ? 'This page needs the admin role in this workspace. Ask an admin of this workspace to change your role.'
              : 'Editing templates needs a template creator or admin role. Ask an admin of this workspace to change your role.'}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/home')} sx={{ borderRadius: 'var(--q-radius-pill, 999px)', textTransform: 'none' }}>
            Back to Home
          </Button>
        </Box>
      </Box>
    );
  }

  return children;
};

export default RoleBasedProtectedRoute;
